import {
	LoadingSpinner,
	ifBlurringParent,
	clientApi,
	spacedToCamelCase,
	useHandler,
	adminUpdateClient,
	deepCopy,
	CustomDropdown,
	CardView,
	useMountEffect,
} from "shared"
import { Button } from "shared/src/components/shadcn/components/atoms/buttons"
import { useState, useMemo, useCallback } from "react"
import { X } from "lucide-react"

type IntegrationOutputObject = {
	name: string
	dataType: string
	integrationType?: Integration
	include: string[]
}
type BullhornIntegrationOutputDataObject = {
	outputFileType?: string
	outputType: "bullhorn"
}
type BhCloudIntegrationOutputDataObject = {
	outputType: "bullhorn_cloud"
}
type SalesforceIntegrationOutputDataObject = {
	outputType: "salesforce"
}
type Seven20IntegrationOutputDataObject = {
	outputType: "seven20"
}

type MercuryIntegrationOutputDataObject = {
	outputType: "mercury"
}

type ArcrmIntegrationOutputDataObject = {
	outputType: "arcrm"
}

type RecmanIntegrationOutputDataObject = {
	outputType: "recman"
}

type VincereIntegrationOutputDataObject = {
	outputType: "vincere"
}

type JobAdderIntegrationOutputDataObject = {
	outputType: "jobadder"
}

type FasttrackIntegrationOutputDataObject = {
	outputType: "fasttrack"
}

type TrackerDocumentType = "resume" | "agencyresume"

type TrackerIntegrationOutputDataObject = {
	outputDocumentType?: TrackerDocumentType
	isPrimary?: boolean
	outputType: "tracker"
}

const trackerDocumentTypes: { key: TrackerDocumentType; label: string }[] = [
	{ key: "resume", label: "CV" },
	{ key: "agencyresume", label: "Agency CV" },
]

type ApiCallIntegrationOutputDataObject = {
	outputType: "apiCall"
	apiCall?: ApiCallParameters
}

type IntegrationOutputDataObject =
	| BullhornIntegrationOutputDataObject
	| BhCloudIntegrationOutputDataObject
	| SalesforceIntegrationOutputDataObject
	| Seven20IntegrationOutputDataObject
	| MercuryIntegrationOutputDataObject
	| ArcrmIntegrationOutputDataObject
	| RecmanIntegrationOutputDataObject
	| VincereIntegrationOutputDataObject
	| JobAdderIntegrationOutputDataObject
	| FasttrackIntegrationOutputDataObject
	| TrackerIntegrationOutputDataObject
	| ApiCallIntegrationOutputDataObject

interface ApiCallParameters {
	baseRequestParameters?: object
	includePersistenceData?: string
	outputMapping?: object
	includeFromBody?: object
	includeFromApp?: object
	transform?: object
	url: string
	calls?: Array<{ endpoint?: string } & Partial<Omit<ApiCallParameters, "url">>>
}

type IntegrationObject = IntegrationOutputDataObject & IntegrationOutputObject

type IntegrationSet = { [integrationName: string]: IntegrationObject }

function getOutputDataFromIntegrationObject(integration: IntegrationObject): IntegrationOutputDataObject {
	switch (integration.outputType) {
		case "bullhorn":
			return { outputFileType: integration.outputFileType, outputType: integration.outputType }
		case "tracker":
			return {
				outputDocumentType: integration.outputDocumentType,
				isPrimary: integration.isPrimary,
				outputType: integration.outputType,
			}
		case "apiCall":
			return { apiCall: integration.apiCall, outputType: integration.outputType }
		default:
			return { outputType: integration.outputType }
	}
}

function getOutputObjectFromIntegrationObject(integration: IntegrationObject): IntegrationOutputObject {
	return {
		name: integration.name,
		dataType: integration.dataType,
		integrationType: integration.integrationType,
		include: integration.include,
	}
}

function getCurrentIntegrations(data) {
	return Object.entries<IntegrationOutputObject>(data?.options?.outputs ?? {}).reduce(
		(acc, [key, integration]): { [integrationName: string]: IntegrationObject } => {
			if (integration.integrationType != null) {
				return { ...acc, [key]: { ...integration, ...data?.outputData?.[key] } }
			} else {
				return acc
			}
		},
		{}
	)
}

export function IntegrationsPage({ clientId, data, setData }): JSX.Element {
	const integrations: IntegrationSet = useMemo(() => getCurrentIntegrations(data), [data])
	const setIntegrations = useCallback(
		(integrations: IntegrationSet) => {
			const outputs = Object.fromEntries(
				Object.entries(integrations).map(([key, integration]) => [
					key,
					getOutputObjectFromIntegrationObject(integration),
				])
			)
			const outputData = Object.fromEntries(
				Object.entries(integrations).map(([key, integration]) => [key, getOutputDataFromIntegrationObject(integration)])
			)
			setData({ ...data, options: { ...data.options, outputs }, outputData })
		},
		[data, setData]
	)
	return (
		<div>
			<ApiKeyCreate clientId={clientId} data={data} setData={setData} />
			{Object.entries(integrations).map(([integrationKey, integrationParameters]) => (
				<IntegrationDetails
					clientId={clientId}
					setData={setData}
					data={data}
					integrationParameters={integrationParameters}
					key={integrationParameters.name}
					integrationKey={integrationKey}
				/>
			))}
			<NewIntegration setIntegrations={setIntegrations} clientId={clientId} data={data} setData={setData} />
		</div>
	)
}

type Integration =
	| "bullhorn"
	| "mercury"
	| "recman"
	| "vincere"
	| "standard"
	| "arcrm"
	| "bullhorn_cloud"
	| "salesforce"
	| "seven20"
	| "jobadder"
	| "fasttrack"
	| "tracker"

const availableIntegrations: { key: Integration; label: string }[] = [
	{ key: "bullhorn", label: "Bullhorn" },
	{ key: "bullhorn_cloud", label: "Bullhorn Cloud" },
	{ key: "mercury", label: "Mercury" },
	{ key: "arcrm", label: "Arcrm" },
	{ key: "recman", label: "Recman" },
	{ key: "vincere", label: "Vincere" },
	{ key: "salesforce", label: "Salesforce" },
	{ key: "seven20", label: "Seven20" },
	{ key: "jobadder", label: "JobAdder" },
	{ key: "fasttrack", label: "FastTrack" },
	{ key: "tracker", label: "Tracker" },
	{ key: "standard", label: "Standard" },
]

function IntegrationDetails({ clientId, data, setData, integrationParameters, integrationKey }) {
	switch (integrationParameters.integrationType) {
		case "bullhorn":
			return (
				<BullhornIntegrationDetails
					integrationKey={integrationKey}
					clientId={clientId}
					setData={setData}
					data={data}
					integrationParameters={integrationParameters}
				/>
			)
		case "bullhorn_cloud":
		case "salesforce":
		case "seven20":
			return (
				<SalesforceIntegrationDetails
					integrationKey={integrationKey}
					clientId={clientId}
					setData={setData}
					data={data}
					integrationParameters={integrationParameters}
				/>
			)
		case "mercury":
			return (
				<MercuryIntegrationDetails
					integrationKey={integrationKey}
					clientId={clientId}
					setData={setData}
					data={data}
					integrationParameters={integrationParameters}
				/>
			)
		case "arcrm":
			return (
				<ArcrmIntegrationDetails
					integrationKey={integrationKey}
					clientId={clientId}
					setData={setData}
					data={data}
					integrationParameters={integrationParameters}
				/>
			)
		case "recman":
			return (
				<RecmanIntegrationDetails
					integrationKey={integrationKey}
					clientId={clientId}
					setData={setData}
					data={data}
					integrationParameters={integrationParameters}
				/>
			)
		case "vincere":
			return (
				<VincereIntegrationDetails
					integrationKey={integrationKey}
					clientId={clientId}
					setData={setData}
					data={data}
					integrationParameters={integrationParameters}
				/>
			)
		case "jobadder":
			return (
				<JobAdderIntegrationDetails
					integrationKey={integrationKey}
					clientId={clientId}
					setData={setData}
					data={data}
					integrationParameters={integrationParameters}
				/>
			)
		case "fasttrack":
			return (
				<FasttrackIntegrationDetails
					integrationKey={integrationKey}
					clientId={clientId}
					setData={setData}
					data={data}
					integrationParameters={integrationParameters}
				/>
			)
		case "tracker":
			return (
				<TrackerIntegrationDetails
					integrationKey={integrationKey}
					clientId={clientId}
					setData={setData}
					data={data}
					integrationParameters={integrationParameters}
				/>
			)
		case "standard":
			return (
				<StandardIntegrationDetails
					integrationKey={integrationKey}
					clientId={clientId}
					setData={setData}
					data={data}
					integrationParameters={integrationParameters}
				/>
			)
	}
}
function MercuryIntegrationDetails({ clientId, setData, data, integrationKey, integrationParameters }) {
	const { integrationType, name } = integrationParameters
	const apiKey = useMemo(() => trimApiKey(data?.apiKey), [data])
	return (
		<IntegrationCard
			clientId={clientId}
			data={data}
			setData={setData}
			name={name}
			integrationKey={integrationKey}
			integrationParameters={integrationParameters}>
			<div>
				<b>URL:</b>{" "}
				{apiKey == null || apiKey === "None"
					? "Please create apiKey/secret to view URL"
					: `${process.env.VITE_MAIN_URL}/entrypoint/integration?apiKey=${apiKey}&secret=${data?.secret}&clientId=${clientId}&platform=${integrationType}&externalClientId=<MERCURY_TENANT_ID>&externalId=<MERCURY_CONTACT_ID>`}
			</div>
			{/* <div>
				<b>File Type Name:</b>
				<input
					type="text"
					style={{ minWidth: "fit-content", width: 300 }}
					value={localOutputFileType}
					onBlur={updateOutputFileType}
					onChange={handleLocalOutputFileType}></input>
			</div> */}
		</IntegrationCard>
	)
}

function RecmanIntegrationDetails({ clientId, setData, data, integrationKey, integrationParameters }) {
	const { integrationType, name } = integrationParameters
	const apiKey = useMemo(() => trimApiKey(data?.apiKey), [data])
	const [recmanApiKey, handleRecmanApiKey] = useHandler(data?.externalSecrets?.recmanApiKey)

	const updateIntegration = useCallback(
		(e) => {
			const newRecmanApiKey = e.target.value
			clientApi
				.patch({ clientId, updates: { externalSecrets: { recmanApiKey: newRecmanApiKey } }, changesOnly: true })
				.then(() => {
					setData({ ...data, externalSecrets: { ...data?.externalSecrets, recmanApiKey: newRecmanApiKey } })
				})
				.catch(() => {
					alert("Failed to add. Refresh page to check current data")
				})
		},
		[clientId, data, setData]
	)

	return (
		<IntegrationCard
			clientId={clientId}
			data={data}
			setData={setData}
			name={name}
			integrationKey={integrationKey}
			integrationParameters={integrationParameters}>
			<div>
				<b>URL:</b>{" "}
				{apiKey == null || apiKey === "None"
					? "Please create apiKey/secret to view URL"
					: `${process.env.VITE_MAIN_URL}/entrypoint/integration?apiKey=${apiKey}&secret=${data?.secret}&clientId=${clientId}&platform=${integrationType}&externalClientId=<RECMAN_CLIENT_ID>&externalId=<RECMAN_CANDIDATE_ID>&userId=<RECMAN_USER_ID>`}
			</div>
			<div>
				<b>Recman API key:</b>
				<input
					type="text"
					style={{ minWidth: "fit-content", width: 300 }}
					value={recmanApiKey ?? ""}
					onBlur={updateIntegration}
					onChange={handleRecmanApiKey}></input>
			</div>
		</IntegrationCard>
	)
}

function VincereIntegrationDetails({ clientId, setData, data, integrationKey, integrationParameters }) {
	const { name } = integrationParameters
	const [vincereApiKey, handleVincereApiKey] = useHandler(data?.externalSecrets?.vincereApiKey)
	const [vincereClientId, handleVincereClientId] = useHandler(data?.externalSecrets?.vincereClientId)
	const [vincereTenant, handleVincereTenant] = useHandler(
		data?.externalId != null ? data?.externalId?.split("#")[1] : ""
	)

	const updateIntegrationApiKey = useCallback(
		(e) => {
			const newVincereApiKey = e.target.value.trim()
			validateUuid(newVincereApiKey)
			clientApi
				.patch({
					clientId,
					updates: {
						externalSecrets: { vincereApiKey: newVincereApiKey },
					},
					changesOnly: true,
				})
				.then(() => {
					setData({
						...data,
						externalSecrets: { ...data?.externalSecrets, vincereApiKey: newVincereApiKey },
					})
				})
				.catch(() => {
					alert("Failed to add. Refresh page to check current data")
				})
		},
		[clientId, data, setData]
	)
	const updateIntegrationClientId = useCallback(
		(e) => {
			const newVincereClientId = e.target.value.trim()
			validateUuid(newVincereClientId)
			clientApi
				.patch({
					clientId,
					updates: {
						externalSecrets: { vincereClientId: newVincereClientId },
					},
					changesOnly: true,
				})
				.then(() => {
					setData({
						...data,
						externalSecrets: { ...data?.externalSecrets, vincereClientId: newVincereClientId },
					})
				})
				.catch(() => {
					alert("Failed to add. Refresh page to check current data")
				})
		},
		[clientId, data, setData]
	)

	const updateIntegrationTenant = useCallback(
		(e) => {
			const newVincereTenant = e.target.value.trim()
			clientApi
				.patch({
					clientId,
					updates: {
						externalId: `vnc#${newVincereTenant}`,
					},
					changesOnly: true,
				})
				.then(() => {
					setData({
						...data,
						externalId: `vnc#${newVincereTenant}`,
					})
				})
				.catch(() => {
					alert("Failed to add. Refresh page to check current data")
				})
		},
		[clientId, data, setData]
	)

	return (
		<IntegrationCard
			clientId={clientId}
			data={data}
			setData={setData}
			name={name}
			integrationKey={integrationKey}
			integrationParameters={integrationParameters}>
			<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
				<div>
					<b>Vincere API key:</b>
					<input
						type="text"
						style={{ minWidth: "fit-content", width: 300 }}
						value={vincereApiKey ?? ""}
						onBlur={updateIntegrationApiKey}
						onChange={handleVincereApiKey}></input>
				</div>
				<div>
					<b>Vincere Client ID:</b>
					<input
						type="text"
						style={{ minWidth: "fit-content", width: 300 }}
						value={vincereClientId ?? ""}
						onBlur={updateIntegrationClientId}
						onChange={handleVincereClientId}></input>
				</div>
				<div>
					<b>Vincere Tenant (base url):</b>
					<input
						type="text"
						style={{ minWidth: "fit-content", width: 300 }}
						value={vincereTenant ?? ""}
						onBlur={updateIntegrationTenant}
						onChange={handleVincereTenant}></input>
				</div>
			</div>
		</IntegrationCard>
	)
}
function ArcrmIntegrationDetails({ clientId, setData, data, integrationKey, integrationParameters }) {
	const { name } = integrationParameters
	const [arcrmApiKey, handleArcrmApiKey] = useHandler(data?.externalSecrets?.arcrmApiKey)
	const [arcrmTenant, handleArcrmTenant] = useHandler(data?.externalSecrets?.arcrmTenant)

	const updateIntegrationApiKey = useCallback(
		(e) => {
			const newArcrmApiKey = e.target.value.trim()
			clientApi
				.patch({
					clientId,
					updates: {
						externalSecrets: { arcrmApiKey: newArcrmApiKey },
					},
					changesOnly: true,
				})
				.then(() => {
					setData({
						...data,
						externalSecrets: { ...data?.externalSecrets, arcrmApiKey: newArcrmApiKey },
					})
				})
				.catch(() => {
					alert("Failed to add. Refresh page to check current data")
				})
		},
		[clientId, data, setData]
	)

	const updateIntegrationTenant = useCallback(
		(e) => {
			const newArcrmTenant = e.target.value.trim()
			clientApi
				.patch({
					clientId,
					updates: {
						externalSecrets: { arcrmTenant: newArcrmTenant },
					},
					changesOnly: true,
				})
				.then(() => {
					setData({
						...data,
						externalSecrets: { ...data?.externalSecrets, arcrmTenant: newArcrmTenant },
					})
				})
				.catch(() => {
					alert("Failed to add. Refresh page to check current data")
				})
		},
		[clientId, data, setData]
	)

	return (
		<IntegrationCard
			clientId={clientId}
			data={data}
			setData={setData}
			name={name}
			integrationKey={integrationKey}
			integrationParameters={integrationParameters}>
			<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
				<div>
					<b>Arcrm API key:</b>
					<input
						type="text"
						style={{ minWidth: "fit-content", width: 300 }}
						value={arcrmApiKey ?? ""}
						onBlur={updateIntegrationApiKey}
						onChange={handleArcrmApiKey}></input>
				</div>
				<div>
					<b>Arcrm Tenant (base url):</b>
					<input
						type="text"
						style={{ minWidth: "fit-content", width: 300 }}
						value={arcrmTenant ?? ""}
						onBlur={updateIntegrationTenant}
						onChange={handleArcrmTenant}></input>
				</div>
			</div>
		</IntegrationCard>
	)
}

function FasttrackIntegrationDetails({ clientId, setData, data, integrationKey, integrationParameters }) {
	const { name } = integrationParameters
	const [fasttrackApiKey, handleFasttrackApiKey] = useHandler(data?.externalSecrets?.fasttrackApiKey)
	const [fasttrackBaseUrl, handleFasttrackBaseUrl] = useHandler(
		data?.externalSecrets?.fasttrackBaseUrl ?? "https://api.fasttrack360.com.au/api/v324"
	)
	const [fasttrackConsumerUsername, handleFasttrackConsumerUsername] = useHandler(
		data?.externalSecrets?.fasttrackConsumerUsername
	)
	const [fasttrackUsername, handleFasttrackUsername] = useHandler(data?.externalSecrets?.fasttrackUsername)
	const [fasttrackPassword, handleFasttrackPassword] = useHandler(data?.externalSecrets?.fasttrackPassword)

	const updateIntegrationApiKey = useCallback(
		(e) => {
			const newFasttrackApiKey = e.target.value.trim()
			clientApi
				.patch({
					clientId,
					updates: {
						externalSecrets: { fasttrackApiKey: newFasttrackApiKey },
					},
					changesOnly: true,
				})
				.then(() => {
					setData({
						...data,
						externalSecrets: { ...data?.externalSecrets, fasttrackApiKey: newFasttrackApiKey },
					})
				})
				.catch(() => {
					alert("Failed to add. Refresh page to check current data")
				})
		},
		[clientId, data, setData]
	)

	const updateIntegrationBaseUrl = useCallback(
		(e) => {
			const newFasttrackBaseUrl = e.target.value.trim()
			clientApi
				.patch({
					clientId,
					updates: {
						externalSecrets: { fasttrackBaseUrl: newFasttrackBaseUrl },
					},
					changesOnly: true,
				})
				.then(() => {
					setData({
						...data,
						externalSecrets: { ...data?.externalSecrets, fasttrackBaseUrl: newFasttrackBaseUrl },
					})
				})
				.catch(() => {
					alert("Failed to add. Refresh page to check current data")
				})
		},
		[clientId, data, setData]
	)

	const updateIntegrationConsumerUsername = useCallback(
		(e) => {
			const newFasttrackConsumerUsername = e.target.value.trim()
			clientApi
				.patch({
					clientId,
					updates: {
						externalSecrets: { fasttrackConsumerUsername: newFasttrackConsumerUsername },
					},
					changesOnly: true,
				})
				.then(() => {
					setData({
						...data,
						externalSecrets: { ...data?.externalSecrets, fasttrackConsumerUsername: newFasttrackConsumerUsername },
					})
				})
				.catch(() => {
					alert("Failed to add. Refresh page to check current data")
				})
		},
		[clientId, data, setData]
	)

	const updateIntegrationUsername = useCallback(
		(e) => {
			const newFasttrackUsername = e.target.value.trim()
			clientApi
				.patch({
					clientId,
					updates: {
						externalSecrets: { fasttrackUsername: newFasttrackUsername },
					},
					changesOnly: true,
				})
				.then(() => {
					setData({
						...data,
						externalSecrets: { ...data?.externalSecrets, fasttrackUsername: newFasttrackUsername },
					})
				})
				.catch(() => {
					alert("Failed to add. Refresh page to check current data")
				})
		},
		[clientId, data, setData]
	)

	const updateIntegrationPassword = useCallback(
		(e) => {
			const newFasttrackPassword = e.target.value.trim()
			clientApi
				.patch({
					clientId,
					updates: {
						externalSecrets: { fasttrackPassword: newFasttrackPassword },
					},
					changesOnly: true,
				})
				.then(() => {
					setData({
						...data,
						externalSecrets: { ...data?.externalSecrets, fasttrackPassword: newFasttrackPassword },
					})
				})
				.catch(() => {
					alert("Failed to add. Refresh page to check current data")
				})
		},
		[clientId, data, setData]
	)

	return (
		<IntegrationCard
			clientId={clientId}
			data={data}
			setData={setData}
			name={name}
			integrationKey={integrationKey}
			integrationParameters={integrationParameters}>
			<div>
				<b>URL:</b>{" "}
				{`${process.env.VITE_MAIN_URL}/entrypoint/custom/fasttrack/integration?clientId=${clientId}&sessionId={{sessionId}}&token={{token}}`}
			</div>
			<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
				<div className="integration-option-label">
					<b>FastTrack API key:</b>
					<input
						type="text"
						style={{ minWidth: "fit-content", width: 300 }}
						value={fasttrackApiKey ?? ""}
						onBlur={updateIntegrationApiKey}
						onChange={handleFasttrackApiKey}></input>
				</div>
				{/* <div>
					<b>FastTrack Tenant (base url):</b>
					<input
						type="text"
						style={{ minWidth: "fit-content", width: 300 }}
						value={fasttrackTenant ?? ""}
						onBlur={updateIntegrationTenant}
						onChange={handleFasttrackTenant}></input>
				</div> */}
				<div className="integration-option-label">
					<b>FastTrack API url:</b>
					<input
						type="text"
						style={{ minWidth: "fit-content", width: 300 }}
						value={fasttrackBaseUrl ?? ""}
						onBlur={updateIntegrationBaseUrl}
						onChange={handleFasttrackBaseUrl}></input>
				</div>
				<div className="integration-option-label">
					<b>FastTrack X Consumer Username:</b>
					<input
						type="text"
						style={{ minWidth: "fit-content", width: 300 }}
						value={fasttrackConsumerUsername ?? ""}
						onBlur={updateIntegrationConsumerUsername}
						onChange={handleFasttrackConsumerUsername}></input>
				</div>
				<div className="integration-option-label">
					<b>FastTrack Username:</b>
					<input
						type="text"
						autoComplete="off"
						style={{ minWidth: "fit-content", width: 300 }}
						value={fasttrackUsername ?? ""}
						onBlur={updateIntegrationUsername}
						onChange={handleFasttrackUsername}></input>
				</div>
				<div className="integration-option-label">
					<b>FastTrack Password:</b>
					<input
						type="text"
						autoComplete="off"
						style={{ minWidth: "fit-content", width: 300 }}
						value={fasttrackPassword ?? ""}
						onBlur={updateIntegrationPassword}
						onChange={handleFasttrackPassword}></input>
				</div>
			</div>
		</IntegrationCard>
	)
}

function BullhornIntegrationDetails({ clientId, setData, data, integrationKey, integrationParameters }) {
	const { name, outputFileType } = integrationParameters
	const apiKey = useMemo(() => trimApiKey(data?.apiKey), [data])
	const [localOutputFileType, handleLocalOutputFileType] = useHandler(outputFileType)
	const updateIntegration = useCallback(
		(newOutputs: IntegrationOutputObject, newOutputData) => {
			const newOptions = { ...data.options, outputs: { ...data.options.outputs, ...newOutputs } }
			const newData = { options: newOptions, outputData: { ...data.outputData, ...newOutputData } }
			clientApi
				.patch({ clientId, updates: { options: { ...newData.options }, outputData: { ...newData.outputData } } })
				.then(() => {
					setData({ ...data, ...newData })
				})
				.catch(() => {
					alert("Failed to add. Refresh page to check current data")
				})
		},
		[clientId, data, setData]
	)

	const updateOutputFileType = useCallback(() => {
		updateIntegration({} as IntegrationOutputObject, {
			[integrationKey as string]: { outputFileType: localOutputFileType, outputType: "bullhorn" },
		})
	}, [localOutputFileType, integrationKey, updateIntegration])

	return (
		<IntegrationCard
			clientId={clientId}
			data={data}
			setData={setData}
			integrationKey={integrationKey}
			name={name}
			integrationParameters={integrationParameters}>
			<div>
				<b>URL:</b>{" "}
				{apiKey == null || apiKey === "None"
					? "Please create apiKey/secret to view URL"
					: `${process.env.VITE_MAIN_URL}/entrypoint/integration?platform=bullhorn&secret=${data?.secret}&apiKey=${apiKey}&clientId=${clientId}`}
			</div>
			<div>
				<b>File Type Name:</b>
				<input
					type="text"
					style={{ minWidth: "fit-content", width: 300 }}
					value={localOutputFileType}
					onBlur={updateOutputFileType}
					onChange={handleLocalOutputFileType}></input>
			</div>
		</IntegrationCard>
	)
}

function JobAdderIntegrationDetails({ clientId, setData, data, integrationKey, integrationParameters }) {
	const { name } = integrationParameters

	return (
		<IntegrationCard
			clientId={clientId}
			data={data}
			setData={setData}
			integrationKey={integrationKey}
			name={name}
			integrationParameters={integrationParameters}>
			<div>An admin user will need to log in to HireAra and attach the integration in the settings page.</div>
		</IntegrationCard>
	)
}

function TrackerIntegrationDetails({ clientId, setData, data, integrationKey, integrationParameters }) {
	const { name, outputDocumentType, isPrimary } = integrationParameters
	const apiKey = useMemo(() => trimApiKey(data?.apiKey), [data])

	const updateOutputData = useCallback(
		(updates: Partial<TrackerIntegrationOutputDataObject>) => {
			const newOutputData = {
				...data.outputData,
				[integrationKey as string]: {
					outputDocumentType: outputDocumentType ?? "resume",
					isPrimary: isPrimary ?? false,
					...updates,
					outputType: "tracker",
				},
			}
			clientApi
				.patch({ clientId, updates: { outputData: newOutputData } })
				.then(() => {
					setData({ ...data, outputData: newOutputData })
				})
				.catch(() => {
					alert("Failed to update. Refresh page to check current data")
				})
		},
		[clientId, data, setData, integrationKey, outputDocumentType, isPrimary]
	)

	return (
		<IntegrationCard
			clientId={clientId}
			data={data}
			setData={setData}
			integrationKey={integrationKey}
			name={name}
			integrationParameters={integrationParameters}>
			<div>
				<b>URL:</b>{" "}
				{apiKey == null || apiKey === "None"
					? "Please create apiKey/secret to view URL"
					: `${process.env.VITE_MAIN_URL}/entrypoint/integration?platform=tracker&secret=${data?.secret}&apiKey=${apiKey}&clientId=${clientId}&externalClientId=fdf55048-50d6-41cb-9189-c1ce3233c00c&userId=[[my userid]]&externalId=[[Resource Id]]`}
			</div>
			<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
				<b>Save as:</b>
				<CustomDropdown
					value={outputDocumentType ?? "resume"}
					actions={trackerDocumentTypes}
					bestOption={"Choose document type"}
					onChange={(e) => updateOutputData({ outputDocumentType: e.target.value as TrackerDocumentType })}
				/>
			</div>
			<div>
				<input
					type="checkbox"
					id={`${integrationKey}-isPrimary`}
					checked={isPrimary ?? false}
					onChange={(e) => updateOutputData({ isPrimary: e.target.checked })}
				/>
				<label htmlFor={`${integrationKey}-isPrimary`}> Set as primary (starred) CV</label>
			</div>
		</IntegrationCard>
	)
}

function SalesforceIntegrationDetails({ clientId, setData, data, integrationKey, integrationParameters }) {
	const { name, outputFileType } = integrationParameters
	const platform = integrationParameters.integrationType
	const apiKey = useMemo(() => trimApiKey(data?.apiKey), [data])
	const [installUrls, setInstallUrls] = useState<{
		main: string
		bhExtension: string
	}>(null)

	useMountEffect(() => {
		fetch("/salesforceUrls.json").then(async (response) => {
			const data = await response.json()
			let bhExtension = data?.bhExtension
			let main = data?.main
			if (process.env.VITE_IS_TEST === "true" || process.env.VITE_IS_DEV === "true") {
				bhExtension = data?.bhExtension.replace("login", "test")
				main = data?.main.replace("login", "test")
			}
			setInstallUrls({
				main,
				bhExtension,
			})
		})
	})

	return (
		<IntegrationCard
			clientId={clientId}
			data={data}
			setData={setData}
			integrationKey={integrationKey}
			name={name}
			integrationParameters={integrationParameters}>
			<div>
				<b>{platform === "bullhorn_cloud" ? "URLs:" : "URL:"}</b>{" "}
				{apiKey == null || apiKey === "None" ? (
					"Please create apiKey/secret to view URL"
				) : (
					<div>
						<h6 className="bs-h6">Current installation url:</h6>
						<p>{installUrls?.main ?? "No install url found"}</p>
						{platform === "bullhorn_cloud" && (
							<>
								<h6 className="bs-h6">Current Bullhorn Cloud Extension installation url:</h6>
								<p>{installUrls?.bhExtension ?? "No install url found"}</p>
							</>
						)}
					</div>
				)}
			</div>
			{/* <div>
				<b>File Type Name:</b>
				<input
					type="text"
					style={{ minWidth: "fit-content", width: 300 }}
					value={localOutputFileType}
					onBlur={updateOutputFileType}
					onChange={handleLocalOutputFileType}></input>
			</div> */}
		</IntegrationCard>
	)
}

function StandardIntegrationDetails({ clientId, setData, data, integrationKey, integrationParameters }) {
	const { name, apiCall } = integrationParameters
	const [localOutputUrl, handleLocalOutputUrl] = useHandler(apiCall?.url)
	let [localOutputHeaders, setLocalOutputHeaders] = useState<Array<[string, string]>>(
		Object.entries(apiCall?.baseRequestParameters.headers ?? {})
	)
	const updateIntegration = useCallback(
		(newOutputs: IntegrationOutputObject, newOutputData) => {
			const newOptions = { ...data.options, outputs: { ...data.options.outputs, ...newOutputs } }
			const newData = { options: newOptions, outputData: { ...data.outputData, ...newOutputData } }
			clientApi
				.patch({
					clientId,
					updates: { options: { ...newData.options }, outputData: { ...newData.outputData } },
					changesOnly: true,
				})
				.then(() => {
					setData({ ...data, ...newData })
				})
				.catch(() => {
					alert("Failed to add. Refresh page to check current data")
				})
		},
		[clientId, data, setData]
	)

	const updateOutputHeaders = useCallback(() => {
		updateIntegration({} as IntegrationOutputObject, {
			[integrationKey as string]: {
				outputType: "apiCall",
				apiCall: {
					...data.outputData[integrationKey]?.apiCall,
					baseRequestParameters: {
						...data.outputData[integrationKey]?.apiCall?.baseRequestParameters,
						headers: Object.fromEntries(
							localOutputHeaders.filter(([k, v]) => k.length > 0 && (v === null || v.length > 0))
						), // null value used to delete
					},
				},
			},
		})
	}, [data.outputData, integrationKey, localOutputHeaders, updateIntegration])

	const updateOutputUrl = useCallback(() => {
		updateIntegration({} as IntegrationOutputObject, {
			[integrationKey as string]: {
				outputType: "apiCall",
				apiCall: {
					...data.outputData[integrationKey]?.apiCall,
					url: localOutputUrl,
				},
			},
		})
	}, [updateIntegration, integrationKey, data.outputData, localOutputUrl])

	return (
		<IntegrationCard
			clientId={clientId}
			data={data}
			setData={setData}
			integrationKey={integrationKey}
			name={name}
			integrationParameters={integrationParameters}>
			<div>
				<b>Output Endpoint Url:</b>
				<input
					type="text"
					style={{ minWidth: "fit-content", width: 500 }}
					value={localOutputUrl}
					onBlur={updateOutputUrl}
					onChange={handleLocalOutputUrl}></input>
			</div>
			<div>
				<b>Headers:</b>
				<div
					tabIndex={0}
					onBlur={ifBlurringParent(updateOutputHeaders)}
					style={{ display: "flex", flexDirection: "column", gap: 10 }}>
					{localOutputHeaders.map(([key, value], index) => {
						const handleChange = (isKey) => (e) => {
							const newHeaders = [...localOutputHeaders]
							newHeaders[index] = isKey ? [e.target.value, value] : [key, e.target.value]
							setLocalOutputHeaders(newHeaders)
						}
						return (
							<div key={`header-${index}`} style={{ display: "flex", gap: "5px" }}>
								<input
									type="text"
									style={{ minWidth: "fit-content", width: 300 }}
									value={key}
									onChange={handleChange(true)}></input>
								:
								<input
									type="text"
									style={{ minWidth: "fit-content", width: 300 }}
									value={value}
									onChange={handleChange(false)}></input>
								<div>
									<X
										onClick={() => {
											localOutputHeaders = localOutputHeaders.map(([k, v], i) => (i !== index ? [k, v] : [k, null]))
											updateOutputHeaders()
											setLocalOutputHeaders(localOutputHeaders.filter((_, i) => i !== index))
										}}
										className="clickable"
										size={24}
										style={{ margin: "auto" }}
									/>
								</div>
							</div>
						)
					})}
					<div style={{ textAlign: "center" }}>
						<Button
							onClick={() => {
								setLocalOutputHeaders([...localOutputHeaders, ["", ""]])
							}}>
							Add New Header
						</Button>
					</div>
				</div>
			</div>
		</IntegrationCard>
	)
}

const DatatypeRadioButton = ({
	integrationKey,
	dataType,
	optionName,
	optionKey,
	changeDataType,
}: {
	integrationKey: string
	dataType: string
	optionName: string
	optionKey: string
	changeDataType: (e: React.ChangeEvent<HTMLInputElement>) => void
}) => {
	return (
		<div>
			<input
				style={{ margin: "0 5px" }}
				type="radio"
				id={`${integrationKey}-${optionName}`}
				name={`${integrationKey}-data`}
				value={optionKey}
				checked={dataType === optionKey}
				onChange={changeDataType}
			/>
			<label htmlFor={`${integrationKey}-${optionName}`}>{optionName}</label>
		</div>
	)
}

function IntegrationCard({
	name,
	clientId,
	setData,
	data,
	integrationKey,
	integrationParameters: { integrationType, dataType, include },
	children,
}) {
	const [isDeleting, setIsDeleting] = useState(false)

	const changeDataType = (e) => {
		if (!e.target.checked) {
			return
		}
		const originalData = deepCopy(data)
		const newOutputs = { ...data.options.outputs }
		newOutputs[integrationKey] = { ...newOutputs[integrationKey], dataType: e.target.value }
		const newData = { options: { ...data.options, outputs: newOutputs } }
		setData({ ...data, ...newData })
		clientApi
			.patch({
				clientId,
				updates: { options: { outputs: newOutputs } },
				changesOnly: true,
			})
			.catch(() => {
				setData(originalData)
				alert("Failed to update data type")
			})
	}

	return (
		<CardView style={{ margin: 20 }}>
			<div style={{ position: "absolute", top: 10, right: 10 }}>
				<X
					onClick={() => {
						const newOutputs = { ...data.options.outputs }
						newOutputs[integrationKey] = null
						const newOutputData = { ...data.outputData }
						newOutputData[integrationKey] = null
						const newData = { options: { ...data.options, outputs: newOutputs }, outputData: newOutputData }
						setIsDeleting(true)
						clientApi
							.patch({
								clientId,
								updates: { options: { outputs: newOutputs }, outputData: newOutputData },
								changesOnly: true,
							})
							.then(() => {
								delete newOutputData[integrationKey]
								delete newOutputs[integrationKey]
								setData({ ...data, ...newData })
							})
							.catch(() => {
								alert("Failed to delete. Refresh page to check current data")
							})
							.finally(() => {
								setIsDeleting(false)
							})
					}}
					className="clickable"
					size={24}
				/>
			</div>
			<h4 className="bs-h4">{name}</h4>
			<div>Integration Type: {integrationType}</div>
			<div style={{ display: "flex", gap: 10, alignItems: "baseline", margin: "5px 0 0 0" }}>
				<div>Data type:</div>
				<DatatypeRadioButton
					integrationKey={integrationKey}
					dataType={dataType.replace("&data", "")}
					optionName="PDF"
					optionKey="formattedPdf"
					changeDataType={(e) => {
						const includesData = dataType.includes("&data")
						changeDataType({
							target: { checked: true, value: includesData ? `${e.target.value}&data` : e.target.value },
						} as React.ChangeEvent<HTMLInputElement>)
					}}
				/>
				<DatatypeRadioButton
					integrationKey={integrationKey}
					dataType={dataType.replace("&data", "")}
					optionName="Word"
					optionKey="formattedWord"
					changeDataType={(e) => {
						const includesData = dataType.includes("&data")
						changeDataType({
							target: { checked: true, value: includesData ? `${e.target.value}&data` : e.target.value },
						} as React.ChangeEvent<HTMLInputElement>)
					}}
				/>
				{integrationType === "standard" && (
					<div>
						<input
							style={{ margin: "0 5px" }}
							type="checkbox"
							id={`${integrationKey}-includeData`}
							checked={dataType.includes("&data")}
							onChange={(e) => {
								const baseType = dataType.replace("&data", "")
								changeDataType({
									target: { checked: true, value: e.target.checked ? `${baseType}&data` : baseType },
								} as React.ChangeEvent<HTMLInputElement>)
							}}
						/>
						<label htmlFor={`${integrationKey}-includeData`}>Include parsed data</label>
					</div>
				)}
			</div>
			<div>Data to include: {include}</div>
			<h5 className="bs-h5" style={{ marginTop: 30 }}>
				Integration Information
			</h5>
			{isDeleting ? (
				<>
					<LoadingSpinner isLoading={true} /> Deleting
				</>
			) : (
				children
			)}
		</CardView>
	)
}

function NewIntegration({ setIntegrations, clientId, data, setData }) {
	const [newName, handleNewName] = useHandler<string>(null)
	const [newType, handleNewType] = useHandler<Integration | null>(null)

	const createNewIntegration = () => {
		const name = newName || DEFAULT_INTEGRATION_PARAMETERS[newType].name
		const newIntegrationKey = spacedToCamelCase(name)
		const newIntegration = {
			...DEFAULT_INTEGRATION_PARAMETERS[newType],
			name,
			integrationType: newType,
		}
		setIntegrations((integrations: { [name: string]: IntegrationOutputObject }) => ({
			...integrations,
			[newIntegrationKey]: newIntegration,
		}))
		const newOptions = {
			...data.options,
			outputs: { ...data?.options?.outputs, [newIntegrationKey]: getOutputObjectFromIntegrationObject(newIntegration) },
		}
		const newData = {
			options: newOptions,
			outputData: { ...data.outputData, [newIntegrationKey]: getOutputDataFromIntegrationObject(newIntegration) },
		}
		clientApi
			.patch({ clientId, updates: { options: { ...newData.options }, outputData: { ...newData.outputData } } })
			.then(() => {
				setData({ ...data, ...newData })
			})
			.catch(() => {
				alert("Failed to add. Refresh page to check current data")
			})
	}
	return (
		<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
			<div>New integration: </div>
			<input
				type="text"
				value={newName ?? DEFAULT_INTEGRATION_PARAMETERS[newType]?.name ?? ""}
				onChange={handleNewName}></input>
			<CustomDropdown
				value={newType ?? "-"}
				actions={availableIntegrations}
				bestOption={"Choose Type"}
				onChange={handleNewType}
			/>
			<Button style={{ marginTop: 0 }} loading={false} onClick={createNewIntegration}>
				Create
			</Button>
		</div>
	)
}

const DEFAULT_INTEGRATION_PARAMETERS: {
	[integrationType in Integration]: IntegrationOutputObject & IntegrationOutputDataObject
} = {
	bullhorn: {
		dataType: "formattedPdf",
		include: ["bullhornData"],
		name: "Save to Bullhorn",
		outputFileType: "Formatted Resume (HireAra)",
		outputType: "bullhorn",
	},
	bullhorn_cloud: {
		dataType: "formattedPdf",
		include: ["fileName"],
		name: "Save to Bullhorn Cloud",
		outputType: "bullhorn_cloud",
	},
	salesforce: {
		dataType: "formattedPdf",
		include: ["fileName"],
		name: "Save to Salesforce",
		outputType: "salesforce",
	},
	seven20: {
		dataType: "formattedPdf",
		include: ["fileName"],
		name: "Save to Seven20",
		outputType: "seven20",
	},
	mercury: {
		dataType: "formattedPdf",
		include: ["fileName"],
		name: "Save to Mercury",
		outputType: "mercury",
	},
	arcrm: {
		dataType: "formattedPdf",
		include: ["fileName"],
		name: "Save to ARCRM",
		outputType: "arcrm",
	},
	recman: {
		dataType: "formattedPdf",
		include: ["fileName"],
		name: "Save to Recman",
		outputType: "recman",
	},
	vincere: {
		dataType: "formattedPdf",
		include: ["fileName", "userId"],
		name: "Save to Vincere",
		outputType: "vincere",
	},
	jobadder: {
		dataType: "formattedPdf",
		include: ["fileName"],
		name: "Save to JobAdder",
		outputType: "jobadder",
	},
	fasttrack: {
		dataType: "formattedPdf",
		include: ["fileName"],
		name: "Save to FastTrack",
		outputType: "fasttrack",
	},
	tracker: {
		dataType: "formattedPdf",
		include: ["fileName"],
		name: "Save to Tracker",
		outputDocumentType: "resume",
		isPrimary: false,
		outputType: "tracker",
	},
	standard: {
		dataType: "formattedPdf&data",
		include: ["fileName"],
		name: "Save to Integration",
		outputType: "apiCall",
		apiCall: {
			baseRequestParameters: { headers: {} },
			includePersistenceData: "json",
			outputMapping: {
				json: {
					data: "data",
					files: {
						cv: {
							b64File: "b64File",
							fileMimeType: "mimeType",
						},
					},
				},
			},
			url: "",
		},
	},
}

function ApiKeyCreate({ clientId, data, setData }) {
	const [isLoading, setIsLoading] = useState(false)
	const [isDeleting, setIsDeleting] = useState(false)
	const apiKey = useMemo(() => trimApiKey(data?.apiKey), [data])

	const createApiKey = async () => {
		setIsLoading(true)
		const { apiKey, secret } = await adminUpdateClient({ updateType: "apiKey", clientId, overwrite: true })
		setIsLoading(false)
		setData({ ...data, apiKey, secret })
	}

	const deleteApiKey = async () => {
		const message = "Are you sure you want to delete this API key? This change is irreversible."
		if (confirm(message) === false) {
			return
		}

		setIsDeleting(true)
		try {
			await adminUpdateClient({ updateType: "deleteApiKey", clientId, overwrite: false })
			setData({ ...data, apiKey: "api#None", secret: null })
		} catch (error) {
			alert("Failed to delete API key. Please try again.")
		} finally {
			setIsDeleting(false)
		}
	}

	return apiKey != null && apiKey !== "None" ? (
		<div style={{ margin: 20 }}>
			<div>Client ID: {clientId}</div>
			<div>API Key: {apiKey}</div>
			<div>Secret: {data?.secret}</div>
			<div style={{ marginTop: 10 }}>
				<Button
					loading={isDeleting}
					onClick={deleteApiKey}
					style={{ backgroundColor: "#dc3545", borderColor: "#dc3545", color: "white" }}>
					Delete API Key
				</Button>
			</div>
		</div>
	) : (
		<div>
			<Button loading={isLoading} onClick={createApiKey}>
				Create API Key/Secret
			</Button>
		</div>
	)
}

function validateUuid(value: string): void {
	const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

	if (!uuidRegex.test(value)) {
		alert("Invalid UUID format. Expected format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx")
		throw new Error("Invalid UUID format. Expected format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx")
	}
}

function trimApiKey(apiKey: string): string {
	return apiKey?.startsWith("api#") ? apiKey.slice(4) : apiKey
}
